/** What the two comment boxes (Feedback, point and comment) share. */
import { useLayoutEffect, useRef, useState } from "react";
import type { FeedbackType } from "../../api/feedback";

/** The kinds a report can be. */
export const FEEDBACK_KINDS: ReadonlyArray<{ value: FeedbackType; label: string }> = [
  { value: "bug", label: "Problem" },
  { value: "data_integrity", label: "Wrong data" },
  { value: "suggestion", label: "Idea" },
];

/** A report that is only voice notes says so, since a report must have words (#717). */
export const VOICE_ONLY = "Voice note attached.";

/** Put a voice note's suggested summary into a comment box, after what is already there. */
export const withSummary = (text: string, summary: string): string => (text.trim() ? `${text.trimEnd()}\n\n${summary}` : summary);

/** The tallest a comment box grows before it scrolls. */
const BOX_MAX_PX = 320;

/**
 * A comment box's text, and a ref for its textarea, which grows with what is in it (up to `BOX_MAX_PX`). A voice note's
 * summary (`addSummary`) goes in as a paragraph of its own; setting the value leaves the caret at its end, so typing
 * goes on below it.
 */
export function useCommentBox() {
  const [text, setText] = useState("");
  const box = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, BOX_MAX_PX)}px`;
  }, [text]);
  const addSummary = (summary: string) => setText((now) => withSummary(now, summary));
  return { text, setText, box, addSummary };
}
